import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";

/**
 * OfflineBanner — REQ-UI-023 (offline minimum).
 *
 * Observuje `navigator.onLine` přes `online` / `offline` window events:
 *   - Offline: zobrazí sticky-top banner, pauzne všechny TanStack Query
 *     read-only queries (přes `setDefaultOptions({ networkMode: 'always' })`
 *     by se to nestalo; defaultní `online` mode už refetch zastaví).
 *   - Reconnect: invalide all queries (refetch on reconnect).
 *
 * Destructive akce řeší API client (vrátí "offline" inline error, ne v
 * banneru) — této komponenty se to netýká, jen čte/píše do banner UI.
 */
export function OfflineBanner() {
  const { t } = useTranslation("common");
  const queryClient = useQueryClient();
  const [online, setOnline] = useState<boolean>(() =>
    typeof navigator === "undefined" ? true : navigator.onLine,
  );

  useEffect(() => {
    const goOnline = () => {
      setOnline(true);
      void queryClient.invalidateQueries();
    };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, [queryClient]);

  if (online) return null;

  return (
    <div
      className="sticky top-0 z-[100] border-b border-secondary bg-warning-secondary px-4 py-3 text-center text-md font-semibold text-warning-primary"
      role="status"
      aria-live="polite"
      data-testid="offline-banner"
    >
      {t("offline.banner")}
    </div>
  );
}
